/**
 * Headless agent auto-apply pipeline.
 *
 * After cross-check completes (when the user preference is ON):
 * 1. First-Pass Legal Review: lock cards with Final version + 100% similarity
 * 2. Run Ask Agent on every remaining eligible card (parallel batches)
 * 3. For suggest_edit: stream edits in parallel batches and show on cards
 * 4. Persist recommendation + conversation on each clause
 * 5. Submit-to-Response only after all agent processing finishes (sequential Word applies)
 */

import type {
  ClauseAskAgentAutoResult,
  CrossCheckClause,
} from '../types/crossCheck';
import apiService from '../services/api';
import { buildPlaybookContextFromRows } from './playbookContext';
import type { PlaybookArchiveRow } from './playbookRecordIndex';
import {
  buildClauseAskAgentPrefix,
  clauseHasAskAgentGrounding,
  clauseHasBankOfChangesForAsk,
  clauseHasMatchedPastNegotiations,
  combineAskAgentContext,
  detectAskAgentLanguage,
  formatAskAgentRecommendationAnswer,
  getAskAgentCopy,
  getAskAgentInitialQuestionForSource,
  getAskAgentNoMatchesNotice,
  type AskAgentContextSource,
  type AskAgentDomain,
  type AskAgentRecommendation,
} from './clauseAskAgentContext';
import {
  clauseHasFinalVersionMatch,
  clauseMatchesSimilarityFilter,
  isClauseClosable,
} from './clauseEditorFilters';
import { streamAiSuggestionFromChat } from './crossCheckAiSuggestion';
import { log } from './logging';
import { submitClauseEditedXml } from './submitClauseEditedXml';
import { extractUsageLimitError } from './usageLimitError';
import { createQrProfiler } from './quickResponseDebug';

/**
 * The axios interceptor tags a rejected request `isAuthError` on a 401 it
 * couldn't silently refresh (see services/api.ts) — other long-running
 * pollers already stop on this flag instead of treating it as data. Ask
 * Agent / edit-generation calls never checked it, so a token expiring
 * mid-run looked like an ordinary per-clause failure and got the same
 * permanent skipped_reason: 'error' as a real one.
 */
export function isAuthExpiryError(err: unknown): boolean {
  return Boolean(err && typeof err === 'object' && (err as { isAuthError?: boolean }).isAuthError);
}

export type ReviewMode =
  | 'review_and_respond_redlines'
  | 'first_pass_legal_review';

/**
 * Why a run stopped mid-phase instead of finishing or hitting a real
 * per-clause failure — both are the caller's problem to fix (raise the
 * limit; reconnect), not the clause's, so neither may be persisted as a
 * failure. 'usage_limit': the account hit its cost cap. 'session_expired':
 * the backend auth token expired mid-run (401) — everything from a
 * `getAgentBatchSize` refresh to an in-flight Ask Agent call surfaces this
 * identically to a real answer, so without this check a token expiry looked
 * exactly like "the clause failed" and got the same permanent
 * skipped_reason: 'error' a real failure gets.
 */
export type PipelinePauseReason = 'usage_limit' | 'session_expired';

export type PipelinePauseInfo = {
  reason: PipelinePauseReason;
  message: string;
};

export type AgentAutoApplyProgress = {
  phase: 'closing' | 'asking' | 'editing' | 'submitting' | 'done' | 'error' | 'paused';
  current: number;
  total: number;
  clauseId?: string;
  message: string;
  /** Set when phase === 'paused': why, and what the user needs to do. */
  pause?: PipelinePauseInfo;
};

/**
 * How often the armed auto-apply gate re-checks itself, and for how long.
 *
 * Short enough that the stage starts as soon as its conditions hold rather
 * than on the next poll tick, long enough not to re-render the clause list
 * needlessly while it waits.
 */
export const AGENT_GATE_RETRY_MS = 2000;
export const AGENT_GATE_RETRY_MAX_ATTEMPTS = 90;

/** Pipeline phases shown as extra steps under the cross-check steps. */
export type AgentPhaseKey = 'asking' | 'editing' | 'submitting';

export type AgentPhaseState = {
  // 'paused': stopped mid-phase on a usage-limit hit — distinct from 'error'
  // because it is not a failure of the clauses already processed, and the
  // remaining ones are still eligible for a resumed run once the caller
  // increases the limit (see isClauseEligibleForAgentAutoApply /
  // isAgentEditPending, which never saw these clauses as touched).
  status: 'pending' | 'running' | 'complete' | 'error' | 'paused';
  current: number;
  total: number;
};

export type AgentPhaseProgress = Record<AgentPhaseKey, AgentPhaseState>;

export const AGENT_PHASE_ORDER: AgentPhaseKey[] = ['asking', 'editing', 'submitting'];

export const INITIAL_AGENT_PHASE_PROGRESS: AgentPhaseProgress = {
  asking: { status: 'pending', current: 0, total: 0 },
  editing: { status: 'pending', current: 0, total: 0 },
  submitting: { status: 'pending', current: 0, total: 0 },
};

/** Fold a pipeline progress event into the per-phase step state. */
export function reduceAgentPhaseProgress(
  prev: AgentPhaseProgress,
  progress: AgentAutoApplyProgress,
): AgentPhaseProgress {
  const next: AgentPhaseProgress = {
    asking: { ...prev.asking },
    editing: { ...prev.editing },
    submitting: { ...prev.submitting },
  };
  const finish = (key: AgentPhaseKey) => {
    const st = next[key];
    if (st.status === 'pending' || st.status === 'running') {
      next[key] = { status: 'complete', current: st.total, total: st.total };
    }
  };

  if (progress.phase === 'asking' || progress.phase === 'editing' || progress.phase === 'submitting') {
    const idx = AGENT_PHASE_ORDER.indexOf(progress.phase);
    AGENT_PHASE_ORDER.slice(0, idx).forEach(finish);
    next[progress.phase] = {
      status: 'running',
      current: Math.min(progress.current, progress.total),
      total: progress.total,
    };
  } else if (progress.phase === 'done') {
    // A cancelled run must not show unfinished phases as complete.
    if (!/cancel/i.test(progress.message)) AGENT_PHASE_ORDER.forEach(finish);
  } else if (progress.phase === 'error') {
    AGENT_PHASE_ORDER.forEach((key) => {
      if (next[key].status === 'running') next[key] = { ...next[key], status: 'error' };
    });
  } else if (progress.phase === 'paused') {
    AGENT_PHASE_ORDER.forEach((key) => {
      if (next[key].status === 'running') next[key] = { ...next[key], status: 'paused' };
    });
  }
  return next;
}

/** 0-100 for a phase; a completed phase with nothing to do reads as 100%. */
export function agentPhasePercent(state: AgentPhaseState): number {
  if (state.total > 0) return Math.min(100, Math.round((state.current / state.total) * 100));
  return state.status === 'complete' ? 100 : 0;
}

/**
 * Derive the three step rows' state from the clauses as loaded, instead of
 * from live pipeline progress. Needed because agentPhaseProgress otherwise
 * starts at "pending" on every reload and stays there forever whenever this
 * sub-project's auto-apply run already finished in a previous session — the
 * pipeline never runs again (nothing is pending), so no onProgress event
 * ever arrives to correct it.
 *
 * Caveat: if real work remains and a fresh run actually starts, its own
 * onProgress totals only cover what's left this run (e.g. 89 remaining out
 * of 145), not the cumulative 56+89 — so the displayed count can briefly
 * drop once the live run's first progress event overrides this baseline.
 * That reflects a real limit of per-run totals, not a display bug.
 */
export function computeAgentPhaseProgressFromClauses(
  clauses: CrossCheckClause[],
): AgentPhaseProgress {
  // A phase's true total isn't known until every upstream phase is fully
  // resolved (e.g. how many clauses need editing isn't known until Ask Agent
  // has run on all of them) — until then it must read "pending", not
  // "complete", even though its own done+pending happens to be 0/0 right now.
  const phaseFromCounts = (
    done: number,
    pending: number,
    upstreamFullyResolved: boolean,
  ): AgentPhaseState => {
    const total = done + pending;
    if (!upstreamFullyResolved && total === 0) return { status: 'pending', current: 0, total: 0 };
    if (total === 0 || pending === 0) return { status: 'complete', current: total, total };
    if (done === 0) return { status: 'pending', current: 0, total };
    return { status: 'running', current: done, total };
  };

  const askPending = clauses.filter(isClauseEligibleForAgentAutoApply).length;
  const askDone = clauses.filter((c) => Boolean(c.ask_agent_auto_result)).length;

  const editUniverse = clauses.filter((c) => c.ask_agent_auto_result?.recommendation === 'suggest_edit');
  const editPending = editUniverse.filter(isAgentEditPending).length;
  const editDone = editUniverse.length - editPending;

  // Submitting has a third bucket: isAgentSubmitPending excludes clauses that
  // already failed to apply (apply_error) so they are never auto-retried —
  // but a failed clause isn't "done" either, so it can't just fold into
  // submitDone the way a normal two-bucket phase would.
  const submitUniverse = editUniverse.filter((c) => !isAgentEditPending(c));
  const submitPending = submitUniverse.filter((c) => isAgentSubmitPending(c)).length;
  const submitFailed = submitUniverse.filter((c) => Boolean(c.ask_agent_auto_result?.apply_error))
    .length;
  const submitDone = submitUniverse.length - submitPending - submitFailed;
  const submitUpstreamResolved = askPending === 0 && editPending === 0;

  let submitting: AgentPhaseState;
  if (submitPending > 0) {
    submitting = phaseFromCounts(submitDone, submitPending + submitFailed, submitUpstreamResolved);
  } else if (submitFailed > 0) {
    // Nothing left to retry automatically, but not every clause applied —
    // 'error' so the phase doesn't falsely read as fully complete.
    submitting = { status: 'error', current: submitDone, total: submitUniverse.length };
  } else {
    submitting = phaseFromCounts(submitDone, 0, submitUpstreamResolved);
  }

  return {
    asking: phaseFromCounts(askDone, askPending, true),
    editing: phaseFromCounts(editDone, editPending, askPending === 0),
    submitting,
  };
}

/** Concurrent Ask Agent / suggest-edit stream workers per batch. */
export const AGENT_AUTO_APPLY_BATCH_SIZE = 6;

/**
 * Resolve the Ask Agent / suggested-edit concurrency window from the server —
 * one source of truth (admin-editable per tenant, GET /real-estate/agent-batch-size),
 * not a local-vs-prod client check. A single local engine instance and a Cloud
 * Run deployment simply have their own DB row, so an untouched local setup
 * gets the default (AGENT_AUTO_APPLY_BATCH_SIZE) and only a tenant's own admin
 * needs to raise it once that tenant's backend/engine actually run in the
 * cloud. Falls back to the hardcoded default on any fetch failure (offline,
 * endpoint error, ...) so a flaky call never blocks the run.
 */
export async function resolveAgentBatchSize(): Promise<number> {
  try {
    const res = await apiService.getAgentBatchSize();
    if (res.success && res.data?.batch_size) {
      return res.data.batch_size;
    }
  } catch (err) {
    console.warn(
      `[agentAutoApply] Failed to load agent batch size; using default (${AGENT_AUTO_APPLY_BATCH_SIZE}):`,
      err,
    );
  }
  return AGENT_AUTO_APPLY_BATCH_SIZE;
}

/**
 * Run `worker` over `items` with up to `batchSize` concurrent in-flight calls.
 * Preserves input order in the returned results array.
 */
export async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  worker: (item: T, index: number) => Promise<R>,
  onBatchProgress?: (completed: number, total: number) => void,
  // Runs after each concurrency batch finishes, before the next one starts.
  // Used to persist that batch's results, so a reload mid-run only loses the
  // batch in flight rather than everything since the last full-stage save.
  onBatchComplete?: (batchItems: T[], batchResults: R[]) => Promise<void> | void,
  // Checked before starting each new batch (never mid-batch — in-flight
  // requests always finish). Lets a worker signal "stop launching more work"
  // — e.g. a usage-limit hit — without throwing, which would otherwise mark
  // every in-flight-but-unstarted item as failed instead of simply not-yet-run.
  shouldStop?: () => boolean,
): Promise<R[]> {
  const size = Math.max(1, Math.floor(batchSize) || 1);
  const results: R[] = new Array(items.length);
  let completed = 0;

  for (let start = 0; start < items.length; start += size) {
    if (shouldStop?.()) break;
    const slice = items.slice(start, start + size);
    const batchResults = await Promise.all(
      slice.map((item, offset) => worker(item, start + offset)),
    );
    for (let i = 0; i < batchResults.length; i += 1) {
      results[start + i] = batchResults[i];
      completed += 1;
    }
    if (onBatchComplete) {
      try {
        await onBatchComplete(slice, batchResults);
      } catch (err) {
        // A save failure must not abort the run — the in-memory result still
        // flows to Phase B/C; only the reload-resume guarantee is weakened.
        console.warn('[mapInBatches] onBatchComplete failed:', err);
      }
    }
    onBatchProgress?.(completed, items.length);
  }

  return results;
}

export type AgentAutoApplyClauseUpdate = {
  clauseId: string;
  askAgentAutoResult?: ClauseAskAgentAutoResult;
  suggestedXml?: string | null;
  responseSubmitted?: boolean;
  locked?: boolean;
};

export type RunAgentAutoApplyPipelineArgs = {
  subProjectId: string;
  projectId: string;
  clauses: CrossCheckClause[];
  reviewMode: ReviewMode;
  askDomain: AskAgentDomain;
  projectLabel: string;
  playbookFinalVersionById: Record<string, boolean>;
  playbookPlainTextById: Record<string, string>;
  playbookFileNameById: Record<string, string>;
  onProgress?: (progress: AgentAutoApplyProgress) => void;
  onClauseUpdate?: (update: AgentAutoApplyClauseUpdate) => void;
  /** Abort mid-run (e.g. component unmount). */
  shouldAbort?: () => boolean;
};

export function clauseMatchesAutoCloseCriteria(
  clause: CrossCheckClause,
  finalVersionById: Record<string, boolean>,
): boolean {
  return (
    clauseHasFinalVersionMatch(clause, finalVersionById) &&
    clauseMatchesSimilarityFilter(clause, 100)
  );
}

/**
 * A clause carrying only the old no-grounding skip.
 *
 * Auto mode used to answer "no direct matches" by skipping the clause instead
 * of accepting the archive-search offer, recording nothing but that refusal.
 * It is not an answer, and the rule that produced it is gone, so the clause is
 * asked again — once. A successful ask replaces the skip with a real
 * recommendation, so this cannot re-trigger on the next run.
 *
 * Deliberately narrow. 'error' is a real failure and 'no_context' means there
 * was genuinely nothing to send. 'no_grounding_no_archive' stays permanent
 * too: the pipeline no longer writes it for an archive it merely failed to
 * reach (those clauses are left with no result at all), so when it is written
 * the archive really is empty — and re-deciding that on every reopen of the
 * sub-project would be churn with no possible new answer.
 */
export function isSupersededNoGroundingSkip(clause: CrossCheckClause): boolean {
  const r = clause.ask_agent_auto_result;
  return Boolean(r && r.recommendation === 'unknown' && r.skipped_reason === 'no_grounding');
}

/**
 * The clause carries an agent result worth showing — a saved conversation or
 * an explanation, as opposed to a bare skip.
 *
 * Shared so the Ask Agent panel's own guard and the callers that decide
 * whether to replay the saved answer or start a fresh session cannot drift
 * apart and leave one of them opening a panel the other considers empty.
 */
export function hasSavedAgentAutoResult(clause: CrossCheckClause): boolean {
  const r = clause.ask_agent_auto_result;
  return Boolean(r && ((r.conversation?.length ?? 0) > 0 || r.explanation));
}

export function isClauseEligibleForAgentAutoApply(clause: CrossCheckClause): boolean {
  if (clause.ask_agent_auto_result && !isSupersededNoGroundingSkip(clause)) return false;
  if (clause.lock_status?.locked) return false;
  if (clause.submission_status?.response_submitted) return false;
  if (clause.submission_status?.schedule_submitted) return false;
  return true;
}

/**
 * True when a clause was already asked (has a persisted suggest_edit result)
 * but the suggested-edit XML was never generated — either this is a fresh
 * suggest_edit result, or a prior run's edit generation failed and left the
 * clause with no suggested_xml. Lets the pipeline resume Phase B for it
 * without re-running Ask Agent.
 */
export function isAgentEditPending(clause: CrossCheckClause): boolean {
  const r = clause.ask_agent_auto_result;
  return Boolean(r && r.recommendation === 'suggest_edit' && !r.suggested_xml && !r.skipped_reason);
}

/**
 * True when a clause has a generated suggested edit that was never applied to
 * Word (a prior run's Phase C never reached it, or it failed to apply). Lets
 * the pipeline resume Phase C for it without re-running Ask Agent or Phase B.
 * `responseSubmitted` is the clause's live submission_status — passed
 * separately because the caller may have a fresher value than the clause
 * snapshot (e.g. after a manual "Submit to Response" from the card).
 */
export function isAgentSubmitPending(
  clause: CrossCheckClause,
  responseSubmitted?: boolean,
): boolean {
  const r = clause.ask_agent_auto_result;
  if (!r || r.recommendation !== 'suggest_edit' || !r.suggested_xml) return false;
  if (r.auto_applied) return false;
  // A previous run already tried to apply this edit and failed (paragraph not
  // found, apply failed, ...). Do not retry it automatically on every reload —
  // the failure is very unlikely to be transient (the document hasn't
  // changed), and the card stays available for a manual "Submit to Response".
  if (r.apply_error) return false;
  if (responseSubmitted ?? clause.submission_status?.response_submitted) return false;
  return true;
}

export type AgentSubmitFailure = {
  clauseId: string;
  clauseNumber: string | number | null;
  error: string;
};

/**
 * Clauses whose suggested edit was generated but failed to apply to Word
 * (auto or manual retry), for the "not applied" row list under the submitting
 * step. Derived from persisted data, so it survives reload.
 */
export function getAgentSubmitFailures(clauses: CrossCheckClause[]): AgentSubmitFailure[] {
  return clauses
    .filter((c) => {
      const r = c.ask_agent_auto_result;
      return Boolean(
        r && r.recommendation === 'suggest_edit' && r.apply_error && !r.auto_applied,
      );
    })
    .map((c) => ({
      clauseId: c.id,
      clauseNumber: c.clause_number ?? null,
      error: c.ask_agent_auto_result!.apply_error!,
    }));
}

/** Auto mode always uses "both" when both sources exist; otherwise the available source. */
export function resolveAutoContextSource(clause: CrossCheckClause): AskAgentContextSource {
  const hasBoc = clauseHasBankOfChangesForAsk(clause);
  const hasPast = clauseHasMatchedPastNegotiations(clause);
  if (hasBoc && hasPast) return 'both';
  if (hasBoc && !hasPast) return 'bank_of_changes';
  if (!hasBoc && hasPast) return 'past_negotiations';
  return 'both';
}

export function buildAskAgentContextForClause(
  clause: CrossCheckClause,
  playbookRows: PlaybookArchiveRow[],
  playbookBody: string,
  askDomain: AskAgentDomain,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string>,
  contextSource: AskAgentContextSource = 'both',
): string {
  const clausePrefix = buildClauseAskAgentPrefix(
    clause,
    playbookPlainTextById,
    playbookFileNameById,
    askDomain,
    contextSource,
    playbookRows,
  );
  const includePlaybook =
    contextSource === 'past_negotiations' || contextSource === 'both';
  return combineAskAgentContext(clausePrefix, playbookBody, {
    includePlaybookArchive: includePlaybook,
    contextSource,
  });
}

export type RunAskAgentForClauseResult = {
  recommendation: AskAgentRecommendation;
  explanation: string;
  displayAnswer: string;
  conversation: Array<{ role: 'user' | 'assistant'; content: string }>;
  contextSource: AskAgentContextSource;
  language: 'he' | 'en';
  skippedReason?: string;
};

export async function runAskAgentForClause(args: {
  clause: CrossCheckClause;
  projectId: string;
  projectLabel: string;
  askDomain: AskAgentDomain;
  playbookRows: PlaybookArchiveRow[];
  playbookBody: string;
  playbookPlainTextById: Record<string, string>;
  playbookFileNameById: Record<string, string>;
}): Promise<RunAskAgentForClauseResult> {
  const {
    clause,
    projectId,
    projectLabel,
    askDomain,
    playbookRows,
    playbookBody,
    playbookPlainTextById,
    playbookFileNameById,
  } = args;

  const language = detectAskAgentLanguage(clause);
  const contextSource = resolveAutoContextSource(clause);
  const contextBody = buildAskAgentContextForClause(
    clause,
    playbookRows,
    playbookBody,
    askDomain,
    playbookPlainTextById,
    playbookFileNameById,
    contextSource,
  );

  const hasGrounding = clauseHasAskAgentGrounding(clause, askDomain);
  const hasPlaybook = playbookRows.length > 0;
  const hasClausePrefix = Boolean(contextBody.trim());

  if (!hasClausePrefix && !hasPlaybook) {
    return {
      recommendation: 'unknown',
      explanation: '',
      displayAnswer: '',
      conversation: [],
      contextSource,
      language,
      skippedReason: 'no_context',
    };
  }

  // No direct matches (sales agreement / bank of changes / insights). This is
  // the card the manual flow greets with "no direct matches — shall I search
  // the archive?", and auto mode used to answer that by skipping the clause
  // entirely, leaving a card marked `no_grounding` with no recommendation.
  //
  // Auto mode now takes the offer, which is the point of auto-apply: do what
  // the lawyer would have clicked. Deep research needs an archive to search,
  // so with no playbook records there is still nothing to do — the manual flow
  // says as much rather than offering the choice.
  const deepResearch = !hasGrounding;
  if (deepResearch && !hasPlaybook) {
    return {
      recommendation: 'unknown',
      explanation: '',
      displayAnswer: '',
      conversation: [],
      contextSource,
      language,
      skippedReason: 'no_grounding_no_archive',
    };
  }

  const copy = getAskAgentCopy(language);
  const question = deepResearch
    ? copy.deepResearchQuestion
    : getAskAgentInitialQuestionForSource(contextSource, askDomain, language);
  const res = await apiService.playbookAsk(
    contextBody,
    question,
    [],
    projectLabel,
    projectId,
    language,
  );

  if (!res.success || !res.data?.answer) {
    throw new Error(res.message || res.error || 'No answer returned from Ask Agent');
  }

  const recommendation: AskAgentRecommendation = res.data.recommendation ?? 'unknown';
  const explanation = res.data.answer;
  const displayAnswer = formatAskAgentRecommendationAnswer(
    recommendation,
    explanation,
    language,
  );

  return {
    recommendation,
    explanation,
    displayAnswer,
    // Mirror the thread the lawyer would have produced by hand, so a deep
    // research result reads as the offer, the acceptance and the answer —
    // not as a bare question nobody appears to have asked. The long prompt
    // that was actually sent is not what they would have typed.
    conversation: deepResearch
      ? [
          { role: 'assistant', content: getAskAgentNoMatchesNotice(askDomain, language) },
          { role: 'user', content: copy.deepResearchAffirmativeLabel },
          { role: 'assistant', content: displayAnswer },
        ]
      : [
          { role: 'user', content: question },
          { role: 'assistant', content: displayAnswer },
        ],
    contextSource,
    language,
  };
}

async function persistResult(
  subProjectId: string,
  clauseId: string,
  result: ClauseAskAgentAutoResult,
): Promise<void> {
  await apiService.updateClauseAskAgentAutoResult(subProjectId, clauseId, result);
}

/**
 * Run the full agent auto-apply pipeline for a completed cross-check.
 */
export async function runAgentAutoApplyPipeline(
  args: RunAgentAutoApplyPipelineArgs,
): Promise<{
  processed: number;
  closed: number;
  errors: number;
  submitted?: number;
  applyFailures?: Array<{ clauseId: string; error: string }>;
  /** Set when the run stopped early (usage limit / session expiry), not a normal finish. */
  pauseInfo?: PipelinePauseInfo;
}> {
  const {
    subProjectId,
    projectId,
    clauses,
    reviewMode,
    askDomain,
    projectLabel,
    playbookFinalVersionById,
    playbookPlainTextById,
    playbookFileNameById,
    onProgress,
    onClauseUpdate,
    shouldAbort,
  } = args;

  let closed = 0;
  let processed = 0;
  let errors = 0;
  // Once set, no further batches are launched in any phase and no touched
  // clause is marked done/failed — they stay exactly as a fresh run would see
  // them, so calling this pipeline again after the cause is resolved (limit
  // raised, session reconnected) picks up from this precise point instead of
  // skipping or redoing work.
  // A ref object, not a bare `let`: TS's control-flow narrowing otherwise
  // treats a later reassignment inside an async worker closure as
  // unreachable from an earlier `if (pauseRef.current)` check, narrowing the
  // variable to `undefined` for the rest of the function.
  const pauseRef: { current?: PipelinePauseInfo } = {};
  const isPaused = () => pauseRef.current !== undefined;
  /** Checked in every Ask/Edit catch block, in this order (limit first: a
   *  request can fail both ways at once if the token happened to expire
   *  right as the limit was also hit, and the limit message is more
   *  actionable). Returns true and records the pause if either matched. */
  const checkForPause = (err: unknown): boolean => {
    const usageLimit = extractUsageLimitError(err);
    if (usageLimit) {
      if (!pauseRef.current) pauseRef.current = { reason: 'usage_limit', message: usageLimit.message };
      return true;
    }
    if (isAuthExpiryError(err)) {
      if (!pauseRef.current) {
        pauseRef.current = {
          reason: 'session_expired',
          message: 'Your session expired. Please sign in again.',
        };
      }
      return true;
    }
    return false;
  };

  const abort = () => shouldAbort?.() === true || isPaused();

  // ── First-pass: auto-close Final version + 100% similarity ──────────────
  let workingClauses = [...clauses];

  if (reviewMode === 'first_pass_legal_review') {
    const toClose = workingClauses.filter(
      (c) =>
        isClauseClosable(c) &&
        !c.ask_agent_auto_result &&
        clauseMatchesAutoCloseCriteria(c, playbookFinalVersionById),
    );

    if (toClose.length > 0) {
      onProgress?.({
        phase: 'closing',
        current: 0,
        total: toClose.length,
        message: `Closing ${toClose.length} final-version (100% similarity) card${toClose.length === 1 ? '' : 's'}…`,
      });

      if (!abort()) {
        try {
          const response = await apiService.setClausesLock(
            subProjectId,
            toClose.map((c) => c.id),
            true,
          );
          if (response.success) {
            closed = toClose.length;
            const closedIds = new Set(toClose.map((c) => c.id));
            for (const c of toClose) {
              onClauseUpdate?.({
                clauseId: c.id,
                locked: true,
              });
            }
            workingClauses = workingClauses.map((c) =>
              closedIds.has(c.id)
                ? {
                    ...c,
                    lock_status: {
                      ...(c.lock_status || {}),
                      locked: true,
                      locked_at: new Date().toISOString(),
                    },
                  }
                : c,
            );
          }
        } catch (err) {
          console.error('[agentAutoApply] First-pass auto-close failed:', err);
          onProgress?.({
            phase: 'closing',
            current: 0,
            total: toClose.length,
            message: 'Failed to auto-close final-version cards; continuing with Ask Agent…',
          });
        }
      }
    }
  }

  if (abort()) {
    onProgress?.({ phase: 'done', current: 0, total: 0, message: 'Agent auto-apply cancelled.' });
    return { processed, closed, errors };
  }

  // ── Resume-safe work buckets ─────────────────────────────────────────────
  // A previous run may have been interrupted (reload, sleep, crash) after
  // persisting some clauses at the Ask stage but before the edit or submit
  // stage, because each stage now persists as it goes (see the batch-save
  // calls below). So on every run we must pick work up from wherever each
  // clause's persisted ask_agent_auto_result left off, not just run Ask Agent
  // on clauses that have no result yet.
  const askPending = workingClauses.filter(isClauseEligibleForAgentAutoApply);
  const editPendingExisting = workingClauses.filter(isAgentEditPending);
  const submitPendingExisting = workingClauses.filter((c) =>
    isAgentSubmitPending(c, c.submission_status?.response_submitted),
  );
  const total = askPending.length;

  if (total === 0 && editPendingExisting.length === 0 && submitPendingExisting.length === 0) {
    onProgress?.({
      phase: 'done',
      current: 0,
      total: 0,
      message: closed
        ? `Closed ${closed} card${closed === 1 ? '' : 's'}. No remaining cards for Ask Agent.`
        : 'No cards eligible for Ask Agent.',
    });
    return { processed, closed, errors };
  }

  // Load the playbook archive only if Ask Agent will actually run this pass;
  // resuming pure edit/submit work needs neither playbookRows nor playbookBody.
  const workflowHint =
    askDomain === 'construction-loan' ? ('construction_loan' as const) : ('real_estate' as const);
  let playbookRows: PlaybookArchiveRow[] = [];
  let playbookBody = '(no playbook archive records for this project)';
  /**
   * The archive could not be reached this run — NOT that it is empty.
   *
   * The two used to be indistinguishable: every fetch swallowed its error and
   * returned an empty list, so a timed-out request looked exactly like a
   * project with no playbook records. A clause with no direct matches then
   * recorded a permanent "no archive to search" skip for what was really a
   * transient failure. Observed 2026-10-11: both fetches hit the 30s timeout,
   * and five clauses were written off 60s later.
   */
  let archiveUnreachable = false;
  if (askPending.length > 0) {
    try {
      const archive = await apiService.listPlaybooksForAgentDetailed(projectId, workflowHint);
      playbookRows = archive.rows;
      archiveUnreachable = archive.allAttemptsFailed;
      if (playbookRows.length) {
        playbookBody = buildPlaybookContextFromRows(projectLabel, playbookRows);
      }
    } catch (err) {
      console.warn('[agentAutoApply] Failed to load playbook archive:', err);
      archiveUnreachable = true;
    }
    log('[agentAutoApply] Playbook archive loaded', {
      rows: playbookRows.length,
      archiveUnreachable,
      askPending: askPending.length,
    });
  }

  // Asking without the archive would answer every no-matches clause with a
  // skip that outlives the failure. Leave them untouched instead: they keep no
  // persisted result, so the next run picks them up exactly as it found them.
  if (archiveUnreachable && askPending.length > 0) {
    onProgress?.({
      phase: 'error',
      current: 0,
      total,
      message: 'Could not reach the playbook archive; leaving those cards for the next run.',
    });
  }

  const batchSize = await resolveAgentBatchSize();

  type AskOutcome = {
    clause: CrossCheckClause;
    askResult?: RunAskAgentForClauseResult;
    error?: unknown;
    /** Ask Agent hit a usage limit or an expired session — not a real failure. */
    paused?: boolean;
  };

  // Persisted (or about-to-be-persisted) base result per clause, without
  // suggested_xml yet for suggest_edit — Phase B merges that in once generated.
  // Seeded here from any prior run's persisted results so Phase B/C can pick
  // up resumed clauses without re-asking them.
  const basePersistedByClauseId = new Map<string, ClauseAskAgentAutoResult>();
  for (const clause of editPendingExisting) {
    if (clause.ask_agent_auto_result) {
      basePersistedByClauseId.set(clause.id, clause.ask_agent_auto_result);
    }
  }

  // ── Phase A: Ask Agent in parallel batches ──────────────────────────────
  let askOutcomes: AskOutcome[] = [];
  if (askPending.length > 0 && !archiveUnreachable && !abort()) {
    onProgress?.({
      phase: 'asking',
      current: 0,
      total,
      message: `Ask Agent: 0/${total} (batches of ${batchSize})…`,
    });

    askOutcomes = await mapInBatches(
      askPending,
      batchSize,
      async (clause): Promise<AskOutcome> => {
        if (abort()) {
          return { clause, error: new Error('aborted') };
        }
        try {
          const askResult = await runAskAgentForClause({
            clause,
            projectId,
            projectLabel,
            askDomain,
            playbookRows,
            playbookBody,
            playbookPlainTextById,
            playbookFileNameById,
          });
          return { clause, askResult };
        } catch (err) {
          if (checkForPause(err)) {
            return { clause, error: err, paused: true };
          }
          console.error(`[agentAutoApply] Ask Agent failed for clause ${clause.id}:`, err);
          return { clause, error: err };
        }
      },
      (completed) => {
        onProgress?.({
          phase: 'asking',
          current: completed,
          total,
          message: `Ask Agent: ${completed}/${total}…`,
        });
      },
      // Persist each concurrency batch immediately: a reload mid-Phase-A only
      // re-asks the batch in flight, not everything asked so far this run.
      async (_batchClauses, batchOutcomes) => {
        const batchToPersist: Array<{
          clauseId: string;
          askAgentAutoResult: ClauseAskAgentAutoResult;
        }> = [];
        for (const outcome of batchOutcomes) {
          const { clause, askResult, error, paused } = outcome;
          if (paused) {
            // Do not persist anything for this clause: leave it exactly as a
            // fresh run would see it (isClauseEligibleForAgentAutoApply still
            // true) so resuming once the cause is resolved retries it for
            // real, instead of skipping it as a phantom "error".
            continue;
          }
          if (error || !askResult) {
            errors += 1;
            const failed: ClauseAskAgentAutoResult = {
              recommendation: 'unknown',
              explanation: '',
              conversation: [],
              applied_at: new Date().toISOString(),
              skipped_reason: 'error',
            };
            batchToPersist.push({ clauseId: clause.id, askAgentAutoResult: failed });
            onClauseUpdate?.({ clauseId: clause.id, askAgentAutoResult: failed });
            continue;
          }
          if (askResult.skippedReason) {
            const skipped: ClauseAskAgentAutoResult = {
              recommendation: 'unknown',
              explanation: '',
              conversation: [],
              context_source: askResult.contextSource,
              language: askResult.language,
              applied_at: new Date().toISOString(),
              skipped_reason: askResult.skippedReason,
            };
            batchToPersist.push({ clauseId: clause.id, askAgentAutoResult: skipped });
            onClauseUpdate?.({ clauseId: clause.id, askAgentAutoResult: skipped });
            processed += 1;
            continue;
          }
          // suggest_edit gets suggested_xml merged in by Phase B's own batch
          // save; accept_as_is / decline are already final at this point.
          const base: ClauseAskAgentAutoResult = {
            recommendation: askResult.recommendation,
            explanation: askResult.explanation,
            conversation: askResult.conversation,
            context_source: askResult.contextSource,
            language: askResult.language,
            applied_at: new Date().toISOString(),
          };
          basePersistedByClauseId.set(clause.id, base);
          batchToPersist.push({ clauseId: clause.id, askAgentAutoResult: base });
          onClauseUpdate?.({ clauseId: clause.id, askAgentAutoResult: base });
          processed += 1;
        }
        if (batchToPersist.length > 0) {
          try {
            await apiService.updateClauseAskAgentAutoResultsBatch(subProjectId, batchToPersist);
          } catch (err) {
            console.warn('[agentAutoApply] Failed to persist Ask Agent batch:', err);
          }
        }
      },
      isPaused,
    );
  }

  {
    const pause = pauseRef.current;
    if (pause) {
      onProgress?.({
        phase: 'paused',
        current: processed,
        total,
        message: pause.message,
        pause,
      });
      return { processed, closed, errors, pauseInfo: pause };
    }
  }
  if (abort()) {
    onProgress?.({ phase: 'done', current: 0, total, message: 'Agent auto-apply cancelled.' });
    return { processed, closed, errors };
  }

  // ── Phase B: Stream suggest_edit XML in parallel batches (no Word submit yet)
  const freshEditClauses = askOutcomes
    .filter(
      (o) => o.askResult && !o.askResult.skippedReason && o.askResult.recommendation === 'suggest_edit',
    )
    .map((o) => o.clause);
  const editClauses = [...freshEditClauses, ...editPendingExisting];

  const editXmlByClauseId = new Map<string, string | null>();
  if (editClauses.length > 0 && !abort()) {
    onProgress?.({
      phase: 'editing',
      current: 0,
      total: editClauses.length,
      message: `Generating suggested edits: 0/${editClauses.length}…`,
    });

    await mapInBatches(
      editClauses,
      batchSize,
      async (clause): Promise<string | null> => {
        if (abort()) return null;
        const base = basePersistedByClauseId.get(clause.id);
        if (!base) return null;
        try {
          return await streamAiSuggestionFromChat(projectId, clause, base.conversation, {
            contextSource: base.context_source ?? null,
            askDomain,
            askLanguage: base.language ?? 'he',
            playbookPlainTextById,
            playbookFileNameById,
          });
        } catch (editErr) {
          if (checkForPause(editErr)) {
            // No persist here either way (see the batch-save below), so the
            // clause is already left untouched — isAgentEditPending still
            // sees it as needing an edit on the next run.
            return null;
          }
          console.error(`[agentAutoApply] suggest_edit stream failed for clause ${clause.id}:`, editErr);
          return null;
        }
      },
      (completed) => {
        onProgress?.({
          phase: 'editing',
          current: completed,
          total: editClauses.length,
          message: `Generating suggested edits: ${completed}/${editClauses.length}…`,
        });
      },
      // Persist each batch's generated XML merged onto its already-persisted
      // base result, so a reload mid-Phase-B keeps prior batches' edits.
      async (batchClauses, batchXmls) => {
        const batchToPersist: Array<{
          clauseId: string;
          askAgentAutoResult: ClauseAskAgentAutoResult;
        }> = [];
        for (let i = 0; i < batchClauses.length; i += 1) {
          const clause = batchClauses[i];
          const suggestedXml = batchXmls[i];
          editXmlByClauseId.set(clause.id, suggestedXml ?? null);
          const base = basePersistedByClauseId.get(clause.id);
          if (!base) continue;
          const merged: ClauseAskAgentAutoResult = suggestedXml
            ? { ...base, suggested_xml: suggestedXml }
            : base;
          basePersistedByClauseId.set(clause.id, merged);
          batchToPersist.push({ clauseId: clause.id, askAgentAutoResult: merged });
          onClauseUpdate?.({
            clauseId: clause.id,
            askAgentAutoResult: merged,
            ...(suggestedXml ? { suggestedXml } : {}),
          });
        }
        if (batchToPersist.length > 0) {
          try {
            await apiService.updateClauseAskAgentAutoResultsBatch(subProjectId, batchToPersist);
          } catch (err) {
            console.warn('[agentAutoApply] Failed to persist suggested-edit batch:', err);
          }
        }
      },
      isPaused,
    );
  }

  {
    const pause = pauseRef.current;
    if (pause) {
      onProgress?.({
        phase: 'paused',
        current: processed,
        total: editClauses.length,
        message: pause.message,
        pause,
      });
      return { processed, closed, errors, pauseInfo: pause };
    }
  }

  // ── Phase C: Submit to Response only after all agent processing ─────────
  type SubmitItem = {
    clause: CrossCheckClause;
    suggestedXml: string;
    persisted: ClauseAskAgentAutoResult;
  };

  const toSubmit: SubmitItem[] = [];
  for (const clause of editClauses) {
    const suggestedXml = editXmlByClauseId.get(clause.id);
    const persisted = basePersistedByClauseId.get(clause.id);
    if (suggestedXml && persisted) toSubmit.push({ clause, suggestedXml, persisted });
  }
  for (const clause of submitPendingExisting) {
    const persisted = clause.ask_agent_auto_result;
    if (persisted?.suggested_xml) {
      toSubmit.push({ clause, suggestedXml: persisted.suggested_xml, persisted });
    }
  }

  let submitted = 0;
  const applyFailures: Array<{ clauseId: string; error: string }> = [];

  if (toSubmit.length > 0 && !abort()) {
    onProgress?.({
      phase: 'submitting',
      current: 0,
      total: toSubmit.length,
      message: `Submitting suggested edits: 0/${toSubmit.length}…`,
    });

    const submitLoop = async (
      sharedWordContext?: { context: Word.RequestContext; paragraphs: Word.ParagraphCollection },
    ): Promise<void> => {
    for (let i = 0; i < toSubmit.length; i += 1) {
      if (abort()) break;
      const item = toSubmit[i];
      // `current` counts edits already handled, so the percentage is never ahead.
      onProgress?.({
        phase: 'submitting',
        current: i,
        total: toSubmit.length,
        clauseId: item.clause.id,
        message: `Submitting suggested edits: ${i + 1}/${toSubmit.length}…`,
      });

      // submitClauseEditedXml reports most failures as `applied: false` + an
      // error code (not an exception), so both paths must be handled here.
      let failure: string | null = null;
      const submitProfile = createQrProfiler(`agent-auto-apply Phase C item ${i + 1}/${toSubmit.length}`);
      try {
        const submitResult = await submitClauseEditedXml(
          subProjectId,
          item.clause,
          item.suggestedXml,
          sharedWordContext,
        );
        submitProfile.mark('submitClauseEditedXml total', { applied: submitResult.applied });
        if (submitResult.applied) {
          submitted += 1;
          const updated: ClauseAskAgentAutoResult = {
            ...item.persisted,
            auto_applied: true,
          };
          try {
            await persistResult(subProjectId, item.clause.id, updated);
            submitProfile.mark('persistResult auto_applied');
          } catch {
            /* Word already applied */
          }
          onClauseUpdate?.({
            clauseId: item.clause.id,
            askAgentAutoResult: updated,
            suggestedXml: item.suggestedXml,
            responseSubmitted: true,
          });
        } else {
          failure = submitResult.error || 'apply_failed';
        }
      } catch (submitErr) {
        if (checkForPause(submitErr)) {
          // Do not record apply_error — unlike a real apply failure, this is
          // never auto-retried on reload (see isAgentSubmitPending), so
          // treating a token-expiry blip as one would strand this clause's
          // already-generated suggestion forever. Stop here; it's still
          // eligible once the run is resumed.
          break;
        }
        console.error(
          `[agentAutoApply] Submit to Response failed for clause ${item.clause.id}:`,
          submitErr,
        );
        failure = 'exception';
      }

      if (failure) {
        errors += 1;
        applyFailures.push({ clauseId: item.clause.id, error: failure });
        console.warn(
          `[agentAutoApply] suggested edit NOT applied for clause ${item.clause.id}: ${failure}`,
        );
        // Keep the suggestion on the card (blue icon) and record why it was not applied.
        const failed: ClauseAskAgentAutoResult = { ...item.persisted, apply_error: failure };
        try {
          await persistResult(subProjectId, item.clause.id, failed);
        } catch {
          /* best effort */
        }
        onClauseUpdate?.({
          clauseId: item.clause.id,
          askAgentAutoResult: failed,
          suggestedXml: item.suggestedXml,
        });
      }
    }
    };

    // One Word.run() for the whole submit phase instead of one per clause:
    // paragraphs.load('items') — the only sizable per-call cost profiling
    // found (~2s on this doc, unavoidable per Word.run() since Word's object
    // model has no "get paragraph by index" other than loading the whole
    // collection) — now happens once for N clauses, not N times. Every edit
    // in this phase is an insertText/delete within one paragraph's own range
    // (see applyViaHybridEngine), never adding/removing whole paragraphs, so
    // the cached collection and every clause's paragraphIndex stay valid for
    // the whole run.
    //
    // Nothing before this point in the run needed Word, so a failure here
    // can only be the initial load itself — safe to retry once, from
    // scratch, with the pre-optimization per-clause path (submitLoop with no
    // sharedWordContext). If instead something throws after real progress
    // was made (submitted/applyFailures non-empty), do NOT retry: every
    // per-item failure mode already returns applied:false from inside the
    // loop's own try/catch instead of throwing, so reaching this catch after
    // work has been done means something unexpected happened, and blindly
    // restarting from clause 0 risks reprocessing already-applied clauses.
    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        const setupProfile = createQrProfiler('agent-auto-apply Phase C shared setup');
        paragraphs.load('items');
        await context.sync();
        setupProfile.mark('load paragraphs.items (once for whole run)', {
          clauseCount: toSubmit.length,
        });
        await submitLoop({ context, paragraphs });
      });
    } catch (err) {
      if (submitted === 0 && applyFailures.length === 0) {
        console.warn(
          '[agentAutoApply] Shared Word.run() setup for Phase C failed; falling back to per-clause apply:',
          err,
        );
        await submitLoop(undefined);
      } else {
        console.error(
          '[agentAutoApply] Phase C stopped after partial progress; not retrying to avoid reprocessing already-applied clauses:',
          err,
        );
      }
    }
  }

  {
    const pause = pauseRef.current;
    if (pause) {
      onProgress?.({
        phase: 'paused',
        current: submitted,
        total: toSubmit.length,
        message: pause.message,
        pause,
      });
      return { processed, closed, errors, submitted, applyFailures, pauseInfo: pause };
    }
  }

  const notApplied = applyFailures.length;
  const submitSummary = toSubmit.length
    ? `, ${submitted}/${toSubmit.length} edits applied${notApplied ? ` (${notApplied} not applied)` : ''}`
    : '';

  onProgress?.({
    phase: 'done',
    current: processed,
    total,
    message:
      errors > 0
        ? `Agent auto-apply finished: ${processed}/${total} cards (${errors} error${errors === 1 ? '' : 's'}${closed ? `, ${closed} closed` : ''}${submitSummary}).`
        : `Agent auto-apply finished: ${processed}/${total} cards${closed ? `, ${closed} closed` : ''}${submitSummary}.`,
  });

  return { processed, closed, errors, submitted, applyFailures };
}

/**
 * Whether a suggest_edit result is currently applied to the document.
 * The card's live `response_submitted` wins (so "undo response" flips it back);
 * `auto_applied` is the fallback when the card has no submission status.
 */
export function isAgentSuggestedEditApplied(
  result: ClauseAskAgentAutoResult | null | undefined,
  responseSubmitted?: boolean,
): boolean {
  return Boolean(responseSubmitted ?? result?.auto_applied);
}

/**
 * Icon tone for the card-header agent button:
 * green = accept as-is, red = decline, blue = suggested edit (applied),
 * orange = suggested edit NOT applied, muted = skipped / unknown.
 */
export function agentIconTone(
  result: ClauseAskAgentAutoResult | null | undefined,
  responseSubmitted?: boolean,
): 'green' | 'red' | 'blue' | 'orange' | 'muted' | null {
  if (!result) return null;
  if (result.skipped_reason && result.recommendation === 'unknown') return 'muted';
  switch (result.recommendation) {
    case 'accept_as_is':
      return 'green';
    case 'decline':
      return 'red';
    case 'suggest_edit':
      return isAgentSuggestedEditApplied(result, responseSubmitted) ? 'blue' : 'orange';
    default:
      return 'muted';
  }
}
